import React, { useEffect, useState, FocusEvent } from "react";
import { Card, Button, Form } from "react-bootstrap";
import { CaretDownIcon } from "Components/Icons/CaretDown";
import { CaretRightIcon } from "Components/Icons/CaretRight";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import { EditIcon } from "Components/Icons/EditIcon";
import { PlusIcon } from "Components/Icons/PlusIcon";
import { CheckIcon } from "Components/Icons/CheckIcon";
import { TimesIcon } from "Components/Icons/TimesIcon";
import { CompetencyItem } from "src/reducers/domainCompetencyReducer";

const isTemporaryItem = (ruuid: string): boolean => ruuid.startsWith("temp-");

export interface CompetencyItemContainerProps {
    item: CompetencyItem;
    addItem: (parentRuuid: string | null, level: number) => void;
    deleteItem: (ruuid: string) => void;
    toggleEnabled: (ruuid: string) => void;
    updateNameAndSave: (ruuid: string, name: string) => void;
    toggleOpen: (ruuid: string) => void;
}

export const CompetencyItemContainer = ({
    item,
    addItem,
    deleteItem,
    toggleEnabled,
    updateNameAndSave,
    toggleOpen
}: CompetencyItemContainerProps) => {
    const [editMode, setEditMode] = useState(false);
    const [tempName, setTempName] = useState(item.standard_name);
    const padding = item.standard_level > 3 ? 40 : 20;
    const isOpen = item.isOpen || false;

    const headerStyle = {
        backgroundColor: item.standard_level === 1 ? "#f7f7f7" : "#F2F2F2",
        paddingLeft: item.standard_level > 1 ? (item.standard_level - 1) * padding + "px" : "0",
    };

    useEffect(() => {
        if (isTemporaryItem(item.ruuid)) {
            setEditMode(true);
            const focusItem = document.querySelector(`#${item.ruuid}`);
            if (focusItem) {
                focusItem.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [item.ruuid]);

    const handleSave = async () => {
        if (tempName.trim() === "") {
            setEditMode(false);
            deleteItem(item.ruuid);
            return;
        }

        updateNameAndSave(item.ruuid, tempName);
        setEditMode(false);
    };

    const handleCancel = () => {
        setTempName(item.standard_name);
        setEditMode(false);

        if (isTemporaryItem(item.ruuid) && item.standard_name.trim() === "") {
            deleteItem(item.ruuid);
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (
            !e.relatedTarget ||
            (e.relatedTarget instanceof HTMLElement &&
             !e.relatedTarget.classList.contains("save") &&
             !e.relatedTarget.classList.contains("cancel"))
        ) {
            if (isTemporaryItem(item.ruuid) && tempName.trim() === "") {
                deleteItem(item.ruuid);
            } else if (tempName.trim() !== "" && tempName !== item.standard_name) {
                handleSave();
            } else {
                setEditMode(false);
            }
        }
    };

    const handleAddSubItem = () => {
        if (!isOpen) {
            toggleOpen(item.ruuid);
        }
        addItem(item.ruuid, item.standard_level + 1);
    };

    return (
        <Card id={item.ruuid}>
            <Card.Header style={headerStyle}>
                {item.standard_level <= 3 && (
                    <Button
                        variant="link"
                        onClick={() => toggleOpen(item.ruuid)}
                        disabled={item.children.length === 0}
                    >
                        {isOpen ? (
                            <CaretDownIcon className="drop-icon" size="14" />
                        ) : (
                            <CaretRightIcon className="drop-icon" size="14" />
                        )}
                    </Button>
                )}
                {item.standard_level <= 3 && (
                    <Button
                        variant="link"
                        className="plus-button"
                        onClick={handleAddSubItem}
                    >
                        <PlusIcon className="d-flex" />
                    </Button>
                )}
                {editMode ? (
                    <div className="competency-item-edit-container">
                        <Form.Control
                            type="text"
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            className="mx-2 edit-input"
                            onBlur={handleBlur}
                            autoFocus
                        />
                        <div className="edit-buttons-container">
                            <Button
                                size="sm"
                                variant="outline"
                                className="cancel"
                                onClick={handleCancel}
                            >
                                <TimesIcon />
                            </Button>
                            <Button
                                size="sm"
                                className="save"
                                onClick={handleSave}
                            >
                                <CheckIcon />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <span
                        className="mx-2 competency-item-name"
                        onClick={() => setEditMode(true)}
                    >
                        {item.standard_name}
                    </span>
                )}
                <div className="action-basic-group">
                    {!editMode && (
                        <Button
                            variant="icon"
                            id={`competency-edit-${item.ruuid}`}
                            onClick={() => setEditMode(true)}
                            style={{
                                padding: "0 5px 0 0",
                                fontSize: 18,
                            }}
                        >
                            <EditIcon />
                        </Button>
                    )}
                    <Button
                        variant="icon"
                        id={`competency-delete-${item.ruuid}`}
                        onClick={() => deleteItem(item.ruuid)}
                        style={{
                            padding: "0 5px 0 0",
                            fontSize: 18,
                        }}
                    >
                        <DeleteIcon />
                    </Button>
                </div>
                <div className="toggleContainer">
                    <Form>
                        <Form.Switch
                            id={`switch-${item.ruuid}`}
                            checked={
                                item.school_enabled !== undefined
                                    ? item.school_enabled
                                    : item.enabled
                            }
                            onChange={() => toggleEnabled(item.ruuid)}
                        />
                    </Form>
                </div>
            </Card.Header>
            {item.children.length > 0 && isOpen && (
                <Card.Body>
                    {item.children.map((child) => (
                        <CompetencyItemContainer
                            key={child.ruuid}
                            item={child}
                            addItem={addItem}
                            deleteItem={deleteItem}
                            toggleEnabled={toggleEnabled}
                            updateNameAndSave={updateNameAndSave}
                            toggleOpen={toggleOpen}
                        />
                    ))}
                </Card.Body>
            )}
        </Card>
    );
};
